import { Check, Copy, Facebook, Instagram, MessageCircle, Share2, Twitter } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface ShareButtonProps {
	contractCode: string;
	contractName?: string;
	className?: string;
}

export function ShareButton({ contractCode, contractName = "Contract", className }: ShareButtonProps) {
	const [copied, setCopied] = useState(false);

	// Generate the share URL based on the contract ID
	const shareUrl = `${window.location.origin}/query?contractCode=${contractCode}`;

	// Handle copy to clipboard
	const copyToClipboard = async (showToast = true) => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			if (showToast) {
				toast.success("Link copied", {
					description: "Contract link copied to clipboard"
				});
			}
			setTimeout(() => setCopied(false), 1000);
		}
		catch (error) {
			console.error(error);
			toast.error("Failed to copy", {
				description: "Please try again or copy the link manually"
			});
		}
	};

	// // Share via email
	// const shareViaEmail = () => {
	// 	window.open(
	// 		`mailto:?subject=Check out this ${contractName}&body=I wanted to share this contract with you: ${shareUrl}`
	// 	);
	// };
	// // Share via LinkedIn
	// const shareViaLinkedIn = () => {
	// 	window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
	// };

	// Share via Twitter/X
	const shareViaTwitter = () => {
		window.open(
			`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=Check out this ${contractName}`
		);
	};

	// Share via Facebook
	const shareViaFacebook = () => {
		window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
	};

	// Share via WhatsApp
	const shareViaWhatsApp = () => {
		window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this ${contractName}: ${shareUrl}`)}`);
	};

	// Share via Instagram (opens in a new tab with instructions)
	const shareViaInstagram = () => {
		// Instagram doesn't have a direct web sharing API like other platforms
		// This is a workaround that copies the link and provides instructions
		copyToClipboard(false);
		toast.success("Ready to share on Instagram", {
			description: "Link copied. Open Instagram and paste in your story or DM."
		});
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size="sm" className={className}>
					<Share2 className="h-4 w-4 mr-2" />
					Share
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuItem onClick={() => copyToClipboard()} className="cursor-pointer">
					{copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
					Copy link
				</DropdownMenuItem>
				<DropdownMenuItem onClick={shareViaTwitter} className="cursor-pointer">
					<Twitter className="h-4 w-4 mr-2" />
					Twitter
				</DropdownMenuItem>
				<DropdownMenuItem onClick={shareViaFacebook} className="cursor-pointer">
					<Facebook className="h-4 w-4 mr-2" />
					Facebook
				</DropdownMenuItem>
				<DropdownMenuItem onClick={shareViaWhatsApp} className="cursor-pointer">
					<MessageCircle className="h-4 w-4 mr-2" />
					WhatsApp
				</DropdownMenuItem>
				<DropdownMenuItem onClick={shareViaInstagram} className="cursor-pointer">
					<Instagram className="h-4 w-4 mr-2" />
					Instagram
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
